
import React, { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useInternalAuth } from '@/hooks/useInternalAuth';
import { useDailyBonus } from '@/hooks/useDailyBonus';
import { usePoints } from '@/hooks/usePoints';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import MobileHeader from '@/components/mobile/MobileHeader';
import MobileBottomNav from '@/components/mobile/MobileBottomNav';
import ReferralCodeDisplay from '@/components/points/ReferralCodeDisplay';
import PointsBreakdown from '@/components/points/PointsBreakdown';
import GiftedPointsDisplay from '@/components/points/GiftedPointsDisplay';
import GiftPointsModal from '@/components/points/GiftPointsModal';
import { ProfileCompletionModal } from '@/components/profile/ProfileCompletionModal';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Trophy, Star, Gift, Calendar, TrendingUp, Clock, AlertCircle, ChevronLeft, ChevronRight, Info, ShoppingCart } from 'lucide-react';
import { Navigate, useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { useIsMobile } from '@/hooks/use-mobile';

const ITEMS_PER_PAGE = 20;

const Points = () => {
  const { user: supabaseUser, profile } = useAuth();
  const { user: internalUser, profile: internalProfile } = useInternalAuth();
  const { pointsData, transactions, loading, awardPoints, daysUntilReset, membershipDuration } = usePoints();
  const { canClaimDaily, timeUntilNextClaim, claimDailyBonus, isClaimingDaily } = useDailyBonus();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [showAllTransactions, setShowAllTransactions] = useState(false);
  const [showGiftModal, setShowGiftModal] = useState(false);

  const user = supabaseUser || internalUser;
  const currentProfile = profile || internalProfile;
  const isInternalUser = !!internalUser && !supabaseUser;
  const userRole = currentProfile?.role?.toLowerCase() || '';
  const canGiftPoints = ['coach', 'team-owner', 'venue'].includes(userRole);

  // Ensure page scrolls to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Reset to page 1 when toggling view
  useEffect(() => {
    setCurrentPage(1);
  }, [showAllTransactions]);

  // Redirect if not authenticated
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  const getDisplayName = () => {
    if (isInternalUser && internalUser) {
      return internalUser.username;
    }
    return supabaseUser?.user_metadata?.first_name || 
           supabaseUser?.user_metadata?.username || 
           supabaseUser?.email?.split('@')[0] || 
           'User';
  };

  const getLevelColor = (level: string) => {
    const colors = {
      'Bronze': 'bg-amber-600/20 text-amber-400 border-amber-600/30',
      'Silver': 'bg-gray-400/20 text-gray-300 border-gray-400/30',
      'Gold': 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      'Platinum': 'bg-blue-400/20 text-blue-300 border-blue-400/30',
      'Diamond': 'bg-purple-500/20 text-purple-400 border-purple-500/30'
    };
    return colors[level] || colors['Bronze'];
  };

  const getNextLevelThreshold = (currentLevel: string) => {
    const thresholds = {
      'Bronze': 1000,
      'Silver': 2000,
      'Gold': 3000,
      'Platinum': 5000,
      'Diamond': null
    };
    return thresholds[currentLevel];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-black">
        <div className="text-white">Loading...</div>
      </div>
    );
  }


  const currentPoints = pointsData?.total_points || 0;
  const currentLevel = pointsData?.current_level || 'Bronze';
  const nextThreshold = getNextLevelThreshold(currentLevel);
  const progressPercentage = nextThreshold ? (currentPoints / nextThreshold) * 100 : 100;

  // Pagination logic (handle null transactions)
  const safeTransactions = transactions ?? [];
  const displayedTransactions = showAllTransactions ? safeTransactions : safeTransactions.slice(0, 10);
  const paginatedTransactions = displayedTransactions.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );
  const totalPages = Math.ceil(displayedTransactions.length / ITEMS_PER_PAGE);

  // Check if user has already received profile completion bonus
  const hasProfileCompletionBonus = safeTransactions.some(
    (transaction) => transaction.transaction_type === 'profile_complete'
  );

  // Check if user has already received welcome bonus
  const hasWelcomeBonus = safeTransactions.some(
    (transaction) => transaction.transaction_type === 'welcome_bonus'
  );

  const handleWelcomeBonus = async () => {
    if (hasWelcomeBonus) {
      toast.error('Welcome bonus already claimed');
      return;
    }

    try {
      console.log('Manually claiming welcome bonus');
      
      // Check one more time right before awarding to prevent race conditions
      const userType = isInternalUser ? 'internal' : 'supabase';
      const { data: existingTransaction } = await supabase
        .from('points_transactions')
        .select('id')
        .eq('user_id', user.id)
        .eq('user_type', userType)
        .eq('transaction_type', 'welcome_bonus')
        .maybeSingle();

      if (existingTransaction) {
        toast.error('Welcome bonus already claimed');
        return;
      }

      await awardPoints(25, 'welcome_bonus', 'Welcome to the platform! 🎉');
    } catch (error) {
      console.error('Error awarding welcome bonus:', error);
      toast.error('Failed to award welcome bonus');
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-black">
      {isMobile ? <MobileHeader /> : <Header />}
      
      <main className={`flex-grow w-full ${
        isMobile 
          ? 'pt-16 mb-16 py-6 px-4' 
          : 'mt-24 mb-6 py-6 md:py-12 px-4 md:px-6'
      }`}>
        <div className="max-w-6xl mx-auto space-y-6">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
              {getDisplayName()}'s Points
            </h1>
            <p className="text-gray-400">
              Earn points by engaging with the community and achieving goals
            </p>
          </div>

          {/* Points Overview */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <Card className="bg-[#0d1117] border border-gray-800">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-gray-400">Total Points</CardTitle>
                <Trophy className="h-4 w-4 text-sports-green" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-white">{currentPoints.toLocaleString()}</div>
                <p className="text-xs text-gray-400">Lifetime earned</p>
              </CardContent>
            </Card>

            <Card className="bg-[#0d1117] border border-gray-800">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-gray-400">Current Level</CardTitle>
                <Star className="h-4 w-4 text-sports-green" />
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2">
                  <Badge className={`text-sm ${getLevelColor(currentLevel)}`}>
                    {currentLevel}
                  </Badge>
                </div>
                {nextThreshold && (
                  <>
                    <div className="w-full bg-gray-800 rounded-full h-2 mt-2">
                      <div 
                        className="bg-sports-green h-2 rounded-full transition-all duration-300"
                        style={{ width: `${Math.min(progressPercentage, 100)}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-400 mt-1">
                      {nextThreshold - currentPoints} points to next level
                    </p>
                  </>
                )}
              </CardContent>
            </Card>

            <Card className="bg-[#0d1117] border border-gray-800">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-gray-400">Rank</CardTitle>
                <TrendingUp className="h-4 w-4 text-sports-green" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-white">
                  #{pointsData?.rank_position || 'Unranked'}
                </div>
                <p className="text-xs text-gray-400">Global ranking</p>
              </CardContent>
            </Card>
          </div>

          {/* Quick Actions */}
          <Card className="bg-[#0d1117] border border-gray-800">
            <CardHeader>
              <CardTitle className="text-sports-green">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-4">
              <Button
                onClick={claimDailyBonus}
                disabled={!canClaimDaily || isClaimingDaily}
                className={`${
                  canClaimDaily 
                    ? 'bg-sports-green text-black hover:opacity-90' 
                    : 'bg-gray-600 text-gray-400 cursor-not-allowed'
                }`}
              >
                <Gift className="h-4 w-4 mr-2" />
                {isClaimingDaily 
                  ? 'Claiming...' 
                  : canClaimDaily 
                    ? 'Claim Daily Bonus (10 pts)' 
                    : `Daily Bonus (${timeUntilNextClaim})`
                }
              </Button>
              {!hasWelcomeBonus && (
                <Button
                  variant="outline"
                  className="border-purple-600 text-purple-400 hover:bg-purple-600/10"
                  onClick={handleWelcomeBonus}
                >
                  <Star className="h-4 w-4 mr-2" />
                  Welcome Bonus (25 pts)
                </Button>
              )}
              {!hasProfileCompletionBonus && (
                <Button
                  variant="outline"
                  className="border-gray-700 text-white hover:bg-gray-800 hover:scale-105 transition-all duration-200"
                  onClick={() => setShowCompletionModal(true)}
                >
                  <Trophy className="h-4 w-4 mr-2" />
                  Complete Profile (50 pts)
                </Button>
              )}
              <Button
                variant="outline"
                className="border-blue-600 text-blue-400 hover:bg-blue-600/10"
                onClick={() => navigate('/points/buy')}
              >
                <ShoppingCart className="h-4 w-4 mr-2" />
                Buy Points
              </Button>
              {canGiftPoints && (pointsData?.giftable_balance || 0) > 0 && (
                <Button
                  variant="outline"
                  className="border-purple-600 text-purple-400 hover:bg-purple-600/10"
                  onClick={() => setShowGiftModal(true)}
                >
                  <Gift className="h-4 w-4 mr-2" />
                  Gift Points ({pointsData?.giftable_balance || 0})
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Referral Code Section */}
          {pointsData?.referral_code && (
            <ReferralCodeDisplay 
              referralCode={pointsData.referral_code}
              referredCount={0}
            />
          )}

          {/* Recent Activity */}
          <Card className="bg-[#0d1117] border border-gray-800">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-6">
              <div>
                <CardTitle className="text-sports-green flex items-center gap-2 mb-2">
                  <Calendar className="h-5 w-5" />
                  Points Activity (Current Year)
                </CardTitle>
                {membershipDuration && (
                  <p className="text-sm text-gray-400 flex items-center gap-2">
                    <Info className="h-4 w-4" />
                    Member for {membershipDuration}
                  </p>
                )}
              </div>
              {safeTransactions.length > 10 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowAllTransactions(!showAllTransactions)}
                  className="border-gray-700 hover:bg-gray-800"
                >
                  {showAllTransactions ? 'Show Recent' : 'View All Activity'}
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {/* Year Reset Advisory */}
              {daysUntilReset > 0 && (
                <Alert className="bg-yellow-900/20 border-yellow-500/30 mb-6">
                  <AlertCircle className="h-5 w-5 text-yellow-500" />
                  <AlertTitle className="text-yellow-500">Points Reset Information</AlertTitle>
                  <AlertDescription className="text-gray-300">
                    Your points activity resets after 1 year. You have <strong className="text-yellow-400">{daysUntilReset} days</strong> remaining in your current points year. 
                    After reset, your points history will be preserved and you'll start fresh. {' '}
                    <Link to="/faq#points-reset" className="text-sports-green underline hover:text-sports-green/80">
                      Learn more in our FAQ
                    </Link>.
                  </AlertDescription>
                </Alert>
              )}
              
              {safeTransactions.length > 0 ? (
                <>
                  <div className="space-y-3">
                    {paginatedTransactions.map((transaction) => (
                      <div key={transaction.id} className="flex items-center justify-between py-2 border-b border-gray-800 last:border-b-0">
                        <div>
                          <p className="text-white font-medium">
                            {transaction.description || transaction.transaction_type.replace(/_/g, ' ')}
                          </p>
                          <p className="text-gray-400 text-sm flex items-center gap-2">
                            <Clock className="h-3 w-3" />
                            {new Date(transaction.created_at).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        </div>
                        <div className="text-sports-green font-bold">
                          +{transaction.points} pts
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Pagination Controls */}
                  {totalPages > 1 && (
                    <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-800">
                      <Button
                        variant="outline"
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        className="border-gray-700 hover:bg-gray-800"
                      >
                        <ChevronLeft className="h-4 w-4 mr-2" />
                        Previous
                      </Button>
                      <span className="text-gray-400 text-sm">
                        Page {currentPage} of {totalPages} ({displayedTransactions.length} total)
                      </span>
                      <Button
                        variant="outline"
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                        className="border-gray-700 hover:bg-gray-800"
                      >
                        Next
                        <ChevronRight className="h-4 w-4 ml-2" />
                      </Button>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-400">No transactions yet. Start earning points!</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
      
      {/* Profile Completion Modal */}
      <ProfileCompletionModal
        isOpen={showCompletionModal}
        onClose={() => setShowCompletionModal(false)}
        onProceed={() => {
          setShowCompletionModal(false);
          navigate('/profile/complete');
        }}
        userProfile={currentProfile || {}}
        userRole={currentProfile?.role || 'player'}
      />
      
      {!isMobile && <Footer />}
      <MobileBottomNav />
    </div>
  );
};

export default Points;
